import { getAllComponents } from '../components/registry';
import { ComponentCategory, ComponentDef } from '../components/types';

const CATEGORY_ORDER: ComponentCategory[] = [
  'component',
  'function',
  'optimization',
  'training',
  'parallelism',
  'multimodal'
];

const CATEGORY_META: Record<ComponentCategory, { label: string; tag: string }> = {
  component: { label: 'Components', tag: 'COMP' },
  function: { label: 'Functions', tag: 'FN' },
  optimization: { label: 'Optimization', tag: 'OPT' },
  multimodal: { label: 'Multimodal', tag: 'MM' },
  training: { label: 'Training', tag: 'TRN' },
  parallelism: { label: 'Parallelism', tag: 'PAR' }
};

export function renderHome(container: HTMLElement): void {
  const components = getAllComponents();
  const groups = groupByCategory(components);

  const wrapper = document.createElement('div');
  wrapper.className = 'home';

  const search = createSearchBar();
  wrapper.appendChild(search.bar);

  const board = document.createElement('div');
  board.className = 'chip-board';

  for (const category of CATEGORY_ORDER) {
    const items = groups[category];
    if (!items || items.length === 0) continue;
    const meta = CATEGORY_META[category] ?? { label: category, tag: category.slice(0, 3).toUpperCase() };
    board.appendChild(createCategoryBand(meta.label, meta.tag, items));
  }

  wrapper.appendChild(board);
  container.appendChild(wrapper);

  bindSearchShortcuts(wrapper, search.input, board);
}

interface SearchHandle {
  bar: HTMLElement;
  input: HTMLInputElement;
}

function createSearchBar(): SearchHandle {
  const bar = document.createElement('div');
  bar.className = 'search-bar';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'Filter · ⌘K';
  input.className = 'search-input';

  const count = document.createElement('span');
  count.className = 'search-count';

  bar.appendChild(input);
  bar.appendChild(count);
  return { bar, input };
}

function createCategoryBand(label: string, tag: string, items: ComponentDef[]): HTMLElement {
  const band = document.createElement('section');
  band.className = 'category-band';

  const header = document.createElement('div');
  header.className = 'band-header';

  const tagEl = document.createElement('span');
  tagEl.className = 'band-tag';
  tagEl.textContent = tag;

  const nameEl = document.createElement('span');
  nameEl.className = 'band-label';
  nameEl.textContent = label;

  const countEl = document.createElement('span');
  countEl.className = 'band-count';
  countEl.textContent = `${items.length}`;

  header.appendChild(tagEl);
  header.appendChild(nameEl);
  header.appendChild(countEl);

  const chips = document.createElement('div');
  chips.className = 'chip-row';
  for (const component of items) {
    chips.appendChild(createChip(component));
  }

  band.appendChild(header);
  band.appendChild(chips);
  return band;
}

function createChip(component: ComponentDef): HTMLElement {
  const chip = document.createElement('a');
  chip.className = 'chip';
  chip.href = `#/component/${component.id}`;
  chip.setAttribute('data-name', component.name.toLowerCase());
  chip.setAttribute('data-desc', (component.description ?? '').toLowerCase());
  chip.title = `${component.name} — ${component.description ?? ''}`;

  const name = document.createElement('span');
  name.className = 'chip-name';
  name.textContent = component.name;

  chip.appendChild(name);
  return chip;
}

function groupByCategory(components: ComponentDef[]): Record<string, ComponentDef[]> {
  const groups: Record<string, ComponentDef[]> = {};
  for (const component of components) {
    const category = component.category ?? 'component';
    if (!groups[category]) groups[category] = [];
    groups[category].push(component);
  }
  return groups;
}

function bindSearchShortcuts(root: HTMLElement, input: HTMLInputElement, board: HTMLElement): void {
  input.addEventListener('input', () => applyFilter(input.value, board));
  root.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      input.focus();
      input.select();
    } else if (e.key === 'Escape' && document.activeElement === input) {
      input.value = '';
      applyFilter('', board);
      input.blur();
    }
  });
}

function applyFilter(raw: string, board: HTMLElement): void {
  const q = raw.trim().toLowerCase();
  let visible = 0;

  board.querySelectorAll<HTMLElement>('.category-band').forEach((band) => {
    let bandVisible = 0;
    band.querySelectorAll<HTMLElement>('.chip').forEach((chip) => {
      const name = chip.getAttribute('data-name') ?? '';
      const desc = chip.getAttribute('data-desc') ?? '';
      const matches = !q || name.includes(q) || desc.includes(q);
      chip.classList.toggle('chip-hidden', !matches);
      if (matches) bandVisible++;
    });
    band.style.display = bandVisible > 0 ? '' : 'none';
    visible += bandVisible;
  });

  const countEl = board.parentElement?.querySelector<HTMLElement>('.search-count');
  if (countEl) {
    countEl.textContent = q ? `${visible}` : '';
  }
}
